<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { motion } from "motion-v";
import { computed, nextTick, useId, useTemplateRef, watch } from "vue";
import { type DecorScreen, nullDecor } from "#ui/decoration/screen";
import TintedIcon from "#ui/primitives/tintedIcon/TintedIcon.vue";
import { isVectorIcon } from "#ui/utils/icon";
import { SIZE_PILL_ICON_SIZE, type SizePillItem } from "./sizePillItem";

export interface Props {
	items: SizePillItem[];
	selectedId?: SizePillItem["id"] | null;
	iconSize?: number;
	decor?: DecorScreen;
}

type Emits = (e: "choose", id: SizePillItem["id"]) => void;

const props = withDefaults(defineProps<Props>(), { iconSize: SIZE_PILL_ICON_SIZE, decor: () => nullDecor });
const emit = defineEmits<Emits>();

const instanceId = useId();
const wrapperRef = useTemplateRef("pillSelectorWrapper");
const { width: wrapperWidth } = useElementSize(wrapperRef);

const maxWidth = computed(() => `${wrapperWidth.value * 0.75}px`);
const layoutId = computed(() => `size-pill-underline-${instanceId}-${props.items.map((item) => item.id).join("-")}`);
const hasIcons = computed(() => props.items.some((item) => !!item.icon));
const isActive = (item: SizePillItem) => props.selectedId === item.id;

const pillLayoutClass = computed(() => (hasIcons.value ? "tw:flex-row tw:gap-10 tw:p-[5px_22px_5px_16px]" : "tw:flex-col tw:gap-2 tw:p-[5px_18px]"));
const pillStateClass = (item: SizePillItem) =>
	isActive(item) ? "size_active tw:[color:var(--size-pill-active-color,#ffffff)]" : "tw:[color:var(--size-pill-color,rgba(255,255,255,0.55))]";

const scrollToElement = (element: unknown) => {
	if (!element) return;
	(element as HTMLElement).scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
};

const scrollToActive = async () => {
	await nextTick();
	requestAnimationFrame(() => {
		const activeElement = wrapperRef.value?.querySelector<HTMLElement>(".size_active");
		if (!activeElement) return;
		scrollToElement(activeElement);
	});
};

const onSelect = (event: MouseEvent, item: SizePillItem) => {
	event.preventDefault();
	event.stopPropagation();
	scrollToElement(event.currentTarget);
	emit("choose", item.id);
};

watch(() => [props.selectedId, props.items], scrollToActive, { immediate: true, deep: true });
watch(wrapperWidth, scrollToActive);
</script>

<template>
	<div
		ref="pillSelectorWrapper"
		class="card_size_place_wrapper tw:relative tw:isolate tw:inline-flex tw:w-full tw:items-center tw:overflow-auto tw:p-[var(--size-pill-padding,4px)]"
	>
		<button
			v-for="item in items"
			:key="item.id"
			type="button"
			class="card_size_place tw:relative tw:z-1 tw:inline-flex tw:h-[var(--size-pill-height,56px)] tw:min-w-56 tw:items-center tw:justify-center tw:rounded-[40px] tw:[flex:var(--size-pill-flex,none)] tw:[border:0] tw:[background:transparent]"
			:class="[pillLayoutClass, pillStateClass(item)]"
			:style="{ 'max-width': maxWidth }"
			v-bind="decor.bind('size_pill', { active: isActive(item) })"
			@click="onSelect($event, item)"
		>
			<TintedIcon
				v-if="hasIcons && item.icon"
				class="tw:relative tw:z-1"
				:src="item.icon"
				:vector="isVectorIcon(item.icon)"
				:color="item.iconColor"
				:size="iconSize"
			/>
			<span class="tw:relative tw:z-1 tw:inline-flex tw:min-w-0 tw:flex-col tw:items-center tw:justify-center tw:gap-2" v-bind="decor.bind('size_pill_texts')">
				<span
					class="card_size tw:relative tw:z-1 tw:w-full tw:truncate tw:leading-none tw:[font-family:var(--size-pill-font-family,var(--fontMain))] tw:[font-size:var(--size-pill-font-size,calc(1rem*var(--fontFit-x,1)))] tw:[text-transform:var(--size-pill-text-transform,none)]"
					v-bind="decor.bind('size_pill_name')"
					>{{ item.name }}</span
				>
				<span
					v-if="item.price"
					class="card_size_price tw:relative tw:z-1 tw:truncate tw:leading-none tw:[font-family:var(--size-pill-price-font-family,var(--fontMain))] tw:[font-size:var(--size-pill-price-font-size,calc(0.875rem*var(--fontFit-x,1)))]"
					v-bind="decor.bind('size_pill_price')"
					>{{ item.price }}</span
				>
			</span>
			<motion.div
				v-if="isActive(item)"
				:transition="{ duration: 0.45, ease: [0.34, 1.3, 0.5, 1] }"
				class="selected-underline tw:absolute tw:inset-0 tw:z-0 tw:rounded-[40px] tw:[background:var(--size-pill-active-bg,var(--accentColor))]"
				:layout-id="layoutId"
			/>
		</button>
	</div>
</template>

<style scoped lang="scss">
  @use './sizePills.scss';
</style>
